import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Launch as LaunchIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import apiService from '../services/api';

const ServiceRegistryPage = () => {
  const [services, setServices] = useState([]);
  const [vms, setVMs] = useState([]);
  const [serviceTypes, setServiceTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  
  // SSH 암호 입력 다이얼로그 상태
  const [sshPasswordDialogOpen, setSshPasswordDialogOpen] = useState(false);
  const [sshPasswordLoading, setSshPasswordLoading] = useState(false);
  const [sshPasswordForm, setSshPasswordForm] = useState({
    service: null,
    vm_name: '',
    vm_ip: '',
    username: '',
    ssh_port: 22,
    password: ''
  });

  const [serviceForm, setServiceForm] = useState({
    vm_name: '',
    service_name: '',
    service_type: 'custom',
    internal_port: '',
    external_path: '',
    protocol: 'http',
    description: '',
    aliases: [],
    enabled: true
  });

  // 서비스 목록 로드
  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVMServices();
      setServices(response.data.services || []);
    } catch (err) {
      
      showNotification('서비스 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 모든 VM 목록 로드 (상태 정보 포함)
  const loadAllVMs = async () => {
    try {
      const response = await apiService.getVMs(true);
      setVMs(response.data.vms || []);
    } catch (err) {
      
      showNotification('VM 목록을 불러오는데 실패했습니다.', 'error');
    }
  };

  // 서비스 타입 목록 로드
  const loadServiceTypes = async () => {
    try {
      const response = await apiService.getVMServiceTypes();
      setServiceTypes(response.data.service_types || {});
    } catch (err) {
      
      showNotification('서비스 타입을 불러오는데 실패했습니다.', 'error');
    }
  };

  useEffect(() => {
    loadServices();
    loadAllVMs();
    loadServiceTypes();
  }, []);

  // 알림 표시
  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  // 알림 닫기
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // 서비스 추가/수정 다이얼로그 열기
  const handleServiceDialogOpen = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        vm_name: service.vm_name,
        service_name: service.service_name,
        service_type: service.service_type || 'custom',
        internal_port: service.internal_port.toString(),
        external_path: service.external_path,
        protocol: service.protocol,
        description: service.description || '',
        aliases: service.aliases || [],
        enabled: service.enabled
      });
    } else {
      setEditingService(null);
      const activeVMs = vms.filter(vm => vm.active || vm.state === 1);
      setServiceForm({
        vm_name: activeVMs.length > 0 ? activeVMs[0].name : '',
        service_name: '',
        service_type: 'custom',
        internal_port: '',
        external_path: '',
        protocol: 'http',
        description: '',
        aliases: [],
        enabled: true
      });
    }
    setServiceDialogOpen(true);
  };

  // 서비스 다이얼로그 닫기
  const handleServiceDialogClose = () => {
    setServiceDialogOpen(false);
    setEditingService(null);
    setServiceForm({
      vm_name: '',
      service_name: '',
      service_type: 'custom',
      internal_port: '',
      external_path: '',
      protocol: 'http',
      description: '',
      aliases: [],
      enabled: true
    });
  };

  // 폼 입력 변경 처리
  const handleFormChange = (e) => {
    const { name, value, checked } = e.target;
    setServiceForm(prev => ({
      ...prev,
      [name]: name === 'enabled' ? checked : value
    }));

    // 서비스 타입이 변경되면 기본값 설정
    if (name === 'service_type' && value && !editingService) {
      const selectedType = serviceTypes[value];
      if (selectedType) {
        setServiceForm(prev => ({
          ...prev,
          internal_port: selectedType.default_port ? selectedType.default_port.toString() : prev.internal_port,
          description: selectedType.description || prev.description,
          aliases: [...selectedType.default_aliases] || []
        }));
      }
    }

    // 서비스 이름이 변경되면 외부 경로 자동 생성
    if (name === 'service_name' && value && !editingService) {
      const cleanServiceName = value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
      setServiceForm(prev => ({
        ...prev,
        external_path: `/vm-services/${cleanServiceName}`
      }));
    }
  };

  // 서비스 저장
  const handleSaveService = async () => {
    try {
      const serviceData = {
        ...serviceForm,
        internal_port: parseInt(serviceForm.internal_port)
      };

      if (editingService) {
        await apiService.updateVMService(editingService.id, serviceData);
        showNotification('서비스 정보가 수정되었습니다.', 'success');
      } else {
        await apiService.createVMService(serviceData);
        showNotification('서비스가 등록되었습니다.', 'success');
      }

      handleServiceDialogClose();
      loadServices();
    } catch (err) {
      
      showNotification(
        `서비스 ${editingService ? '수정' : '등록'} 실패: ${err.response?.data?.detail || err.message}`,
        'error'
      );
    }
  };

  // 서비스 활성화/비활성화 토글
  const handleToggleService = async (service) => {
    try {
      await apiService.toggleVMService(service.id, !service.enabled);
      showNotification(
        `서비스가 ${!service.enabled ? '활성화' : '비활성화'}되었습니다.`,
        'success'
      );
      loadServices();
    } catch (err) {
      
      showNotification(
        `서비스 상태 변경 실패: ${err.response?.data?.detail || err.message}`,
        'error'
      );
    }
  };

  // 서비스 삭제 확인 다이얼로그 열기
  const handleDeleteDialogOpen = (service) => {
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  // 서비스 삭제
  const handleDeleteService = async () => {
    try {
      await apiService.deleteVMService(serviceToDelete.id);
      showNotification('서비스가 삭제되었습니다.', 'success');
      setDeleteDialogOpen(false);
      setServiceToDelete(null);
      loadServices();
    } catch (err) {
      
      showNotification(
        `서비스 삭제 실패: ${err.response?.data?.detail || err.message}`,
        'error'
      );
    }
  };

  // 서비스 URL 생성
  const getServiceUrl = (service) => {
    const currentHost = window.location.host;
    const protocol = service.protocol === 'https' ? 'https' : 'http';
    return `${protocol}://${currentHost}${service.external_path}`;
  };

  // 복잡한 서비스인지 판단
  const isComplexService = (serviceName) => {
    const complexPatterns = [
      'vscode', 'code-server', 'jupyter', 'jupyterlab', 'tensorboard',
      'rstudio', 'theia', 'gitpod', 'codesandbox'
    ];
    
    const name = serviceName.toLowerCase();
    return complexPatterns.some(pattern => name.includes(pattern));
  };

  // VM 내부 브라우저에서 서비스 열기 (토큰 방식 권장)
  const handleOpenServiceWithToken = async (service, sshPassword = null) => {
    try {
      // 복잡한 서비스는 바로 토큰 방식 사용
      if (isComplexService(service.service_name)) {
        showNotification(`복잡한 웹 애플리케이션을 위해 토큰 기반 접근을 사용합니다...`, 'info');
        
        try {
          showNotification(`${service.service_name} 서비스용 보안 토큰을 생성 중...`, 'info');
          const tokenResponse = await apiService.createVMServiceAccessToken(service.id, 30);
          const accessUrl = tokenResponse.data.access_url;
          
          // 새 창에서 토큰 기반 접근
          const newWindow = window.open(accessUrl, '_blank', 'noopener,noreferrer,width=1200,height=800');
          
          if (newWindow) {
            showNotification(`${service.service_name} 서비스가 새 창에서 열렸습니다. (보안 토큰 사용, 30분 유효)`, 'success');
          } else {
            // 팝업이 차단된 경우 사용자에게 수동으로 열도록 안내
            showNotification(
              `팝업이 차단되었습니다. 새 탭에서 서비스에 접근하려면 아래 링크를 Ctrl+클릭하거나 복사하세요: ${accessUrl}`,
              'warning'
            );
            
            // 클립보드에 URL 복사 시도
            if (navigator.clipboard && window.isSecureContext) {
              navigator.clipboard.writeText(accessUrl).then(() => {
                showNotification('접근 URL이 클립보드에 복사되었습니다. 새 탭에 붙여넣기하세요.', 'info');
              }).catch(() => {
                
              });
            }
          }
          return;
        } catch (tokenErr) {
          
          
          const errorMessage = tokenErr.response?.data?.detail || tokenErr.message || '알 수 없는 오류';
          showNotification(`토큰 생성 실패: ${errorMessage}. VM 브라우저 방식을 시도합니다...`, 'warning');
          
          // VM 브라우저 방식으로 fallback
        }
      }
      
      showNotification(`VM '${service.vm_name}' 내부 브라우저에서 서비스를 여는 중...`, 'info');
      
      // VM 내부 브라우저에서 서비스 실행
      const response = await apiService.openServiceInVM(service.id, sshPassword);
      const result = response.data;
      
      if (result.status === 'password_required') {
        // SSH 암호 입력이 필요한 경우
        showNotification('VM SSH 연결을 위한 암호 입력이 필요합니다.', 'warning');
        setSshPasswordForm({
          service: service,
          vm_name: result.vm_name,
          vm_ip: result.vm_ip,
          username: result.username,
          ssh_port: result.ssh_port || 22,
          password: ''
        });
        setSshPasswordDialogOpen(true);
        return;
      }
      
      if (result.status === 'gui_unavailable') {
        // VM에 GUI 환경이나 브라우저가 없는 경우 - 자동으로 토큰 방법 사용
        
        showNotification(result.message, 'warning');
        showNotification(result.fallback_message, 'info');
        
        try {
          showNotification('대안으로 보안 토큰을 생성 중...', 'info');
          const tokenResponse = await apiService.createVMServiceAccessToken(service.id, 30);
          const accessUrl = tokenResponse.data.access_url;
          
          const newWindow = window.open(accessUrl, '_blank', 'noopener,noreferrer,width=1200,height=800');
          if (newWindow) {
            showNotification('보안 토큰으로 서비스를 새 창에서 열었습니다. (30분 유효)', 'success');
          } else {
            showNotification(
              `팝업이 차단되었습니다. 새 탭에서 서비스에 접근하려면 아래 링크를 Ctrl+클릭하거나 복사하세요: ${accessUrl}`,
              'warning'
            );
            
            if (navigator.clipboard && window.isSecureContext) {
              navigator.clipboard.writeText(accessUrl).then(() => {
                showNotification('접근 URL이 클립보드에 복사되었습니다. 새 탭에 붙여넣기하세요.', 'info');
              }).catch(() => {
                
              });
            }
          }
        } catch (fallbackErr) {
          const errorMsg = fallbackErr.response?.data?.detail || fallbackErr.message;
          showNotification(`토큰 기반 접근도 실패했습니다: ${errorMsg}`, 'error');
        }
        return;
      }
      
      showNotification(
        `${service.service_name} 서비스가 VM 내부 브라우저에서 열렸습니다. (로컬 환경처럼 접근)`,
        'success'
      );
      
      
    } catch (err) {
      showNotification(
        `VM 브라우저 실행 실패: ${err.response?.data?.detail || err.message}`,
        'error'
      );
      
      // 실패 시 대안으로 토큰 기반 외부 접근 제공
      try {
        showNotification('대안으로 보안 토큰을 생성하여 접근합니다...', 'info');
        const tokenResponse = await apiService.createVMServiceAccessToken(service.id, 30);
        const accessUrl = tokenResponse.data.access_url;
        
        const newWindow = window.open(accessUrl, '_blank', 'noopener,noreferrer,width=1200,height=800');
        if (newWindow) {
          showNotification('보안 토큰으로 서비스를 새 창에서 열었습니다. (30분 유효)', 'success');
        } else {
          showNotification(
            `팝업이 차단되었습니다. 새 탭에서 서비스에 접근하려면 아래 링크를 Ctrl+클릭하거나 복사하세요: ${accessUrl}`,
            'warning'
          );
          
          if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(accessUrl).then(() => {
              showNotification('접근 URL이 클립보드에 복사되었습니다. 새 탭에 붙여넣기하세요.', 'info');
            }).catch(() => {
              
            });
          }
        }
      } catch (fallbackErr) {
        
        const errorDetail = fallbackErr.response?.data?.detail || fallbackErr.message;
        showNotification(`모든 접근 방법이 실패했습니다: ${errorDetail}`, 'error');
      }
    }
  };

  // SSH 암호 입력 후 다시 시도
  const handleSSHPasswordSubmit = async () => {
    if (sshPasswordLoading) {
      return; // 이미 진행 중이면 중복 요청 방지
    }
    
    try {
      setSshPasswordLoading(true);
      
      // 입력된 암호로 다시 시도
      const response = await apiService.openServiceInVM(sshPasswordForm.service.id, sshPasswordForm.password);
      const result = response.data;
      
      if (result.status === 'success') {
        setSshPasswordDialogOpen(false);
        showNotification(
          `${sshPasswordForm.service.service_name} 서비스가 VM 내부 브라우저에서 열렸습니다.`,
          'success'
        );
        
        // 폼 초기화
        setSshPasswordForm({
          service: null,
          vm_name: '',
          vm_ip: '',
          username: '',
          ssh_port: 22,
          password: ''
        });
      } else if (result.status === 'gui_unavailable') {
        // VM에 GUI 환경이나 브라우저가 없는 경우 - 자동으로 토큰 방법 사용
        setSshPasswordDialogOpen(false);
        showNotification(result.message, 'warning');
        showNotification(result.fallback_message, 'info');
        
        try {
          showNotification('VM 내부 브라우저 불가로 보안 토큰을 생성 중...', 'info');
          const tokenResponse = await apiService.createVMServiceAccessToken(sshPasswordForm.service.id, 30);
          const accessUrl = tokenResponse.data.access_url;
          
          const newWindow = window.open(accessUrl, '_blank', 'noopener,noreferrer,width=1200,height=800');
          if (newWindow) {
            showNotification('보안 토큰으로 서비스를 새 창에서 열었습니다. (30분 유효)', 'success');
          } else {
            showNotification(
              `팝업이 차단되었습니다. 새 탭에서 서비스에 접근하려면 아래 링크를 Ctrl+클릭하거나 복사하세요: ${accessUrl}`,
              'warning'
            );
            
            if (navigator.clipboard && window.isSecureContext) {
              navigator.clipboard.writeText(accessUrl).then(() => {
                showNotification('접근 URL이 클립보드에 복사되었습니다. 새 탭에 붙여넣기하세요.', 'info');
              }).catch(() => {
                
              });
            }
          }
        } catch (fallbackErr) {
          const errorMsg = fallbackErr.response?.data?.detail || fallbackErr.message;
          showNotification(`토큰 기반 접근도 실패했습니다: ${errorMsg}`, 'error');
        }
        
        // 폼 초기화
        setSshPasswordForm({
          service: null,
          vm_name: '',
          vm_ip: '',
          username: '',
          ssh_port: 22,
          password: ''
        });
      } else {
        // 비밀번호가 틀린 경우
        showNotification('입력하신 비밀번호로 SSH 연결에 실패했습니다. 비밀번호를 확인해주세요.', 'error');
        // 다이얼로그는 열어두고 비밀번호만 초기화
        setSshPasswordForm(prev => ({ ...prev, password: '' }));
      }
      
    } catch (err) {
      showNotification('SSH 연결 중 오류가 발생했습니다.', 'error');
      // 다이얼로그는 열어두고 비밀번호만 초기화
      setSshPasswordForm(prev => ({ ...prev, password: '' }));
    } finally {
      setSshPasswordLoading(false);
    }
  };

  // SSH 암호 다이얼로그 닫기
  const handleSSHPasswordDialogClose = () => {
    if (sshPasswordLoading) {
      return; // 로딩 중일 때는 닫기 방지
    }
    setSshPasswordDialogOpen(false);
    setSshPasswordLoading(false);
    setSshPasswordForm({
      service: null,
      vm_name: '',
      vm_ip: '',
      username: '',
      ssh_port: 22,
      password: ''
    });
  };

  // 폼 유효성 검사
  const isFormValid = () => {
    return (
      serviceForm.vm_name &&
      serviceForm.service_name &&
      serviceForm.internal_port &&
      serviceForm.external_path &&
      parseInt(serviceForm.internal_port) > 0 &&
      parseInt(serviceForm.internal_port) <= 65535
    );
  };

  // 서비스 상태 표시
  const getServiceStatusChip = (service) => {
    if (!service.enabled) {
      return <Chip label="비활성화" color="default" size="small" />;
    }
    
    const vm = vms.find(v => v.name === service.vm_name);
    if (!vm) {
      return <Chip label="VM 없음" color="error" size="small" />;
    }
    
    // VM 상태 확인 (state 또는 active 필드 사용)
    const isVMActive = vm.active || vm.state === 1; // state 1 = running
    if (!isVMActive) {
      return <Chip label="VM 중지됨" color="warning" size="small" />;
    }
    
    return <Chip label="활성" color="success" size="small" />;
  };

  // 프로토콜 표시
  const getProtocolChip = (protocol) => (
    <Chip
      label={protocol.toUpperCase()}
      color={protocol === 'https' ? 'success' : 'primary'}
      size="small"
      variant="outlined"
    />
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          VM 서비스 레지스트리
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadServices();
              loadAllVMs();
            }}
            sx={{ mr: 1 }}
          >
            새로고침
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleServiceDialogOpen()}
            disabled={vms.filter(vm => vm.active || vm.state === 1).length === 0}
          >
            서비스 등록
          </Button>
        </Box>
      </Box>

      {/* VM이 없는 경우 안내 */}
      {vms.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          등록된 VM이 없습니다. 먼저 VM을 생성하고 시작한 후 서비스를 등록하세요.
        </Alert>
      )}
      
      {/* 실행 중인 VM이 없는 경우 안내 */}
      {vms.length > 0 && vms.filter(vm => vm.active || vm.state === 1).length === 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          실행 중인 VM이 없습니다. 서비스를 등록하려면 먼저 VM을 시작하세요.
        </Alert>
      )}

      {/* 통계 정보 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {services.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                총 등록된 서비스
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {services.filter(s => s.enabled).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                활성화된 서비스
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {vms.filter(vm => vm.active || vm.state === 1).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                실행 중인 VM
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {services.filter(s => {
                  if (!s.enabled) return false;
                  const vm = vms.find(v => v.name === s.vm_name);
                  return !vm || (!vm.active && vm.state !== 1);
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                접근 불가 서비스
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 서비스 목록 테이블 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>서비스명</TableCell>
                <TableCell>VM</TableCell>
                <TableCell>내부 포트</TableCell>
                <TableCell>외부 경로</TableCell>
                <TableCell>프로토콜</TableCell>
                <TableCell>상태</TableCell>
                <TableCell>설명</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
                      등록된 서비스가 없습니다.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                services.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {service.service_name}
                        </Typography>
                        {isComplexService(service.service_name) && (
                          <Chip 
                            label="고급" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {service.vm_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {service.internal_port}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Link
                        href={getServiceUrl(service)}
                        sx={{ 
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          textDecoration: service.enabled ? 'underline' : 'none',
                          color: service.enabled ? 'primary.main' : 'text.disabled'
                        }}
                      >
                        {service.external_path}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {getProtocolChip(service.protocol)}
                    </TableCell>
                    <TableCell>
                      {getServiceStatusChip(service)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {service.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={
                        isComplexService(service.service_name) 
                          ? "토큰 기반 안전 접근 (고급 웹 애플리케이션)" 
                          : "VM 내부 브라우저에서 서비스 열기"
                      }>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenServiceWithToken(service)}
                          disabled={!service.enabled}
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={service.enabled ? "비활성화" : "활성화"}>
                        <IconButton
                          color={service.enabled ? "warning" : "success"}
                          onClick={() => handleToggleService(service)}
                        >
                          {service.enabled ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="서비스 수정">
                        <IconButton
                          color="primary"
                          onClick={() => handleServiceDialogOpen(service)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="서비스 삭제">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteDialogOpen(service)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 서비스 추가/수정 다이얼로그 */}
      <Dialog open={serviceDialogOpen} onClose={handleServiceDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingService ? '서비스 정보 수정' : '새 서비스 등록'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>VM</InputLabel>
            <Select
              name="vm_name"
              value={serviceForm.vm_name}
              onChange={handleFormChange}
              label="VM"
            >
              {vms.filter(vm => vm.active || vm.state === 1).map((vm) => (
                <MenuItem key={vm.name} value={vm.name}>
                  {vm.name} (실행 중)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>서비스 타입</InputLabel>
            <Select
              name="service_type"
              value={serviceForm.service_type}
              onChange={handleFormChange}
              label="서비스 타입"
            >
              {Object.entries(serviceTypes).map(([key, type]) => (
                <MenuItem key={key} value={key}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            fullWidth
            label="서비스명"
            name="service_name"
            value={serviceForm.service_name}
            onChange={handleFormChange}
            helperText="예: web-server, api, database"
          />

          <TextField
            margin="normal"
            fullWidth
            label="내부 포트"
            name="internal_port"
            type="number"
            value={serviceForm.internal_port}
            onChange={handleFormChange}
            helperText="VM 내부에서 실행되는 서비스의 포트 번호"
            inputProps={{ min: 1, max: 65535 }}
          />

          <TextField
            margin="normal"
            fullWidth
            label="외부 경로"
            name="external_path"
            value={serviceForm.external_path}
            onChange={handleFormChange}
            helperText="외부에서 접근할 URL 경로 (예: /vm-services/my-app)"
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>프로토콜</InputLabel>
            <Select
              name="protocol"
              value={serviceForm.protocol}
              onChange={handleFormChange}
              label="프로토콜"
            >
              <MenuItem value="http">HTTP</MenuItem>
              <MenuItem value="https">HTTPS</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              별칭 ({serviceForm.aliases.length}개)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {serviceForm.aliases.map((alias, index) => (
                <Chip
                  key={index}
                  label={alias}
                  onDelete={() => {
                    setServiceForm(prev => ({
                      ...prev,
                      aliases: prev.aliases.filter((_, i) => i !== index)
                    }));
                  }}
                  size="small"
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              서비스 타입 선택 시 자동으로 설정됩니다. 필요한 경우 수정할 수 있습니다.
            </Typography>
          </Box>

          <TextField
            margin="normal"
            fullWidth
            label="설명 (선택사항)"
            name="description"
            value={serviceForm.description}
            onChange={handleFormChange}
            helperText="서비스에 대한 설명"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleServiceDialogClose}>취소</Button>
          <Button
            onClick={handleSaveService}
            variant="contained"
            disabled={!isFormValid()}
          >
            {editingService ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>서비스 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            정말 이 서비스를 삭제하시겠습니까?
          </Typography>
          {serviceToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>서비스명:</strong> {serviceToDelete.service_name}
              </Typography>
              <Typography variant="body2">
                <strong>VM:</strong> {serviceToDelete.vm_name}
              </Typography>
              <Typography variant="body2">
                <strong>경로:</strong> {serviceToDelete.external_path}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDeleteService} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* SSH 암호 입력 다이얼로그 */}
      <Dialog open={sshPasswordDialogOpen} onClose={handleSSHPasswordDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>VM SSH 연결</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            VM 내부 브라우저에서 서비스를 열기 위해 SSH 연결이 필요합니다.
          </Typography>
          
          {sshPasswordForm.service && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>서비스:</strong> {sshPasswordForm.service.service_name}
              </Typography>
              <Typography variant="body2">
                <strong>VM:</strong> {sshPasswordForm.vm_name} ({sshPasswordForm.vm_ip}:{sshPasswordForm.ssh_port || 22})
              </Typography>
              <Typography variant="body2">
                <strong>사용자:</strong> {sshPasswordForm.username}
              </Typography>
            </Box>
          )}
          
          <TextField
            margin="normal"
            fullWidth
            label="SSH 암호"
            name="password"
            type="password"
            value={sshPasswordForm.password}
            onChange={(e) => setSshPasswordForm(prev => ({ ...prev, password: e.target.value }))}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && sshPasswordForm.password.trim()) {
                handleSSHPasswordSubmit();
              }
            }}
            helperText="VM에 SSH 접속할 때 사용하는 암호를 입력하세요"
            autoFocus
          />
          
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSSHPasswordDialogClose} disabled={sshPasswordLoading}>취소</Button>
          <Button
            onClick={handleSSHPasswordSubmit}
            variant="contained"
            disabled={!sshPasswordForm.password.trim() || sshPasswordLoading}
            startIcon={sshPasswordLoading ? <CircularProgress size={20} /> : null}
          >
            {sshPasswordLoading ? '연결 중...' : '연결'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 알림 스낵바 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceRegistryPage;